import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './components.css';
import NeuralNetworkVisualizer from './neural-network-visualizer';

function AITrainer() {
  const [studentState, setStudentState] = useState([50, 70, 50, 0]);
  const [targetTaxonomy, setTargetTaxonomy] = useState('understand');
  const [steps, setSteps] = useState(5);
  const [planningMethod, setPlanningMethod] = useState('beam_search');
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Learning style states
  const [visualValue, setVisualValue] = useState(80);
  const [auditoryValue, setAuditoryValue] = useState(60);
  const [kinestheticValue, setKinestheticValue] = useState(50);
  const [readingWritingValue, setReadingWritingValue] = useState(65);
  const [dominantStyle, setDominantStyle] = useState({ name: 'Visual', effectiveness: 75 });
  const [showStudentResponseModal, setShowStudentResponseModal] = useState(false);
  const [neuralNetworkVisible, setNeuralNetworkVisible] = useState(false);
  
  // Neural network visualization
  const neuralNetworkRef = useRef(null);
  const canvasRef = useRef(null);
  
  const taxonomyLevels = ['remember', 'understand', 'apply', 'analyze', 'evaluate', 'create'];
  const planningMethods = [
    { id: 'beam_search', name: 'Beam Search' },
    { id: 'monte_carlo', name: 'Monte Carlo' },
    { id: 'reward_based', name: 'Reward-Based' }
  ];

  useEffect(() => {
    // Update learning styles whenever one of the values changes
    updateLearningStyles();
  }, [visualValue, auditoryValue, kinestheticValue, readingWritingValue]);
  
  // Initialize and update neural network visualization when visible
  useEffect(() => {
    if (neuralNetworkVisible && canvasRef.current && !neuralNetworkRef.current) {
      // Initialize neural network visualization
      neuralNetworkRef.current = NeuralNetworkVisualizer.create('neural-canvas', {
        layerCount: 4,
        neuronsPerLayer: 8,
        dominantStyle: dominantStyle.name
      }).start();
    } else if (neuralNetworkVisible && neuralNetworkRef.current) {
      // Update configuration when learning styles change
      neuralNetworkRef.current.updateConfig({
        dominantStyle: dominantStyle.name
      });
    } else if (!neuralNetworkVisible && neuralNetworkRef.current) {
      // Stop animation when not visible
      neuralNetworkRef.current.stop();
      neuralNetworkRef.current = null;
    }
    
    // Cleanup function
    return () => {
      if (neuralNetworkRef.current) {
        neuralNetworkRef.current.stop();
        neuralNetworkRef.current = null;
      }
    };
  }, [neuralNetworkVisible, dominantStyle.name]);

  const updateLearningStyles = () => {
    // Calculate effectiveness based on slider values
    const visualEffectiveness = calculateEffectiveness(visualValue, 85);
    const auditoryEffectiveness = calculateEffectiveness(auditoryValue, 75);
    const kinestheticEffectiveness = calculateEffectiveness(kinestheticValue, 70);
    const readingWritingEffectiveness = calculateEffectiveness(readingWritingValue, 80);
    
    // Find dominant learning style
    const styles = [
      { name: 'Visual', value: visualValue, effectiveness: visualEffectiveness },
      { name: 'Auditory', value: auditoryValue, effectiveness: auditoryEffectiveness },
      { name: 'Kinesthetic', value: kinestheticValue, effectiveness: kinestheticEffectiveness },
      { name: 'Reading/Writing', value: readingWritingValue, effectiveness: readingWritingEffectiveness }
    ];
    
    const newDominantStyle = styles.reduce((max, style) => (style.value > max.value ? style : max), styles[0]);
    setDominantStyle(newDominantStyle);
    
    // Update student state based on learning styles
    const newKnowledgeValue = Math.min(100, studentState[0] + 
      (newDominantStyle.effectiveness / 100 * 10));
    
    const newState = [...studentState];
    newState[0] = Math.round(newKnowledgeValue);
    setStudentState(newState);
  };
  
  const calculateEffectiveness = (preferenceValue, baseEffectiveness) => {
    // A function to calculate teaching effectiveness based on preference value
    // Higher preference means higher potential effectiveness
    const rawEffectiveness = (baseEffectiveness * (1 + (preferenceValue - 50) / 100));
    return Math.min(98, Math.max(30, rawEffectiveness));
  };

  const handleStateChange = (index, value) => {
    const newState = [...studentState];
    newState[index] = parseInt(value);
    setStudentState(newState);
  };
  
  const handleStyleChange = (style, value) => {
    switch(style) {
      case 'visual':
        setVisualValue(parseInt(value));
        break;
      case 'auditory':
        setAuditoryValue(parseInt(value));
        break;
      case 'kinesthetic':
        setKinestheticValue(parseInt(value));
        break;
      case 'reading-writing':
        setReadingWritingValue(parseInt(value));
        break;
      default:
        break;
    }
  };

  const generatePlan = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post('/api/plan', {
        student_state: studentState,
        target_taxonomy: targetTaxonomy,
        steps: parseInt(steps),
        planning_method: planningMethod,
        learning_styles: {
          visual: visualValue / 100,
          auditory: auditoryValue / 100,
          kinesthetic: kinestheticValue / 100,
          reading_writing: readingWritingValue / 100
        }
      });
      
      setPlan(response.data);
      setNeuralNetworkVisible(true);
      setLoading(false);
    } catch (err) {
      console.error('Error generating plan:', err);
      setError('Failed to generate teaching plan');
      setLoading(false);
    }
  };

  return (
    <div className="ai-trainer">
      <h1>AI Teaching Planner</h1>
      
      <div className="trainer-content">
        <div className="row">
          <div className="col-md-6">
            <div className="card mb-4">
              <div className="card-header">Student Model</div>
              <div className="card-body">
                <div className="state-controls">
                  <div className="state-control">
                    <label>Knowledge:</label>
                    <div className="d-flex align-items-center">
                      <input 
                        type="range" 
                        className="form-range flex-grow-1 me-2"
                        min="0" 
                        max="100" 
                        value={studentState[0]} 
                        onChange={(e) => handleStateChange(0, e.target.value)} 
                      />
                      <span className="badge bg-primary state-value">{studentState[0]}%</span>
                    </div>
                  </div>
                  
                  <div className="state-control mt-3">
                    <label>Engagement:</label>
                    <div className="d-flex align-items-center">
                      <input 
                        type="range" 
                        className="form-range flex-grow-1 me-2"
                        min="0" 
                        max="100" 
                        value={studentState[1]} 
                        onChange={(e) => handleStateChange(1, e.target.value)} 
                      />
                      <span className="badge bg-success state-value">{studentState[1]}%</span>
                    </div>
                  </div>
                  
                  <div className="state-control mt-3">
                    <label>Confusion:</label>
                    <div className="d-flex align-items-center">
                      <input 
                        type="range" 
                        className="form-range flex-grow-1 me-2"
                        min="0" 
                        max="100" 
                        value={studentState[2]} 
                        onChange={(e) => handleStateChange(2, e.target.value)} 
                      />
                      <span className="badge bg-danger state-value">{studentState[2]}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span>Teaching Goals</span>
                <span className="badge bg-success">Win State: Active</span>
              </div>
              <div className="card-body">
                <div className="goal-controls">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="taxonomy" className="form-label">Target Taxonomy Level:</label>
                      <select 
                        id="taxonomy"
                        className="form-select"
                        value={targetTaxonomy} 
                        onChange={(e) => setTargetTaxonomy(e.target.value)}
                      >
                        {taxonomyLevels.map(level => (
                          <option key={level} value={level}>{level.charAt(0).toUpperCase() + level.slice(1)}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label htmlFor="steps" className="form-label">Number of Steps:</label>
                      <input 
                        id="steps" 
                        type="number"
                        className="form-control"
                        min="1" 
                        max="10" 
                        value={steps} 
                        onChange={(e) => setSteps(e.target.value)} 
                      />
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label htmlFor="planning" className="form-label">Planning Method:</label>
                    <select 
                      id="planning"
                      className="form-select"
                      value={planningMethod} 
                      onChange={(e) => setPlanningMethod(e.target.value)}
                    >
                      {planningMethods.map(method => (
                        <option key={method.id} value={method.id}>{method.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="d-grid">
                    <button 
                      className="btn btn-primary" 
                      onClick={generatePlan} 
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Generating...
                        </>
                      ) : 'Generate Teaching Plan'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-6">
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span>Learning Style Adaptation</span>
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => setShowStudentResponseModal(true)}
                >
                  Simulate Student Response
                </button>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <h5 className="text-center mb-2">Visual Learning</h5>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-eye-fill text-success fs-3 me-2"></i>
                      <div className="w-100">
                        <input 
                          type="range" 
                          className="form-range"
                          min="0" 
                          max="100" 
                          value={visualValue} 
                          onChange={(e) => handleStyleChange('visual', e.target.value)} 
                        />
                        <div className="d-flex justify-content-between">
                          <span className="small">Low</span>
                          <span className="fw-bold">{visualValue}%</span>
                          <span className="small">High</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="d-flex justify-content-between">
                        <small>Effectiveness:</small>
                        <small>{calculateEffectiveness(visualValue, 85).toFixed(0)}%</small>
                      </div>
                      <div className="progress" style={{ height: '8px' }}>
                        <div 
                          className="progress-bar bg-success" 
                          role="progressbar" 
                          style={{ width: `${calculateEffectiveness(visualValue, 85)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <h5 className="text-center mb-2">Auditory Learning</h5>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-volume-up-fill text-primary fs-3 me-2"></i>
                      <div className="w-100">
                        <input 
                          type="range" 
                          className="form-range"
                          min="0" 
                          max="100" 
                          value={auditoryValue} 
                          onChange={(e) => handleStyleChange('auditory', e.target.value)} 
                        />
                        <div className="d-flex justify-content-between">
                          <span className="small">Low</span>
                          <span className="fw-bold">{auditoryValue}%</span>
                          <span className="small">High</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="d-flex justify-content-between">
                        <small>Effectiveness:</small>
                        <small>{calculateEffectiveness(auditoryValue, 75).toFixed(0)}%</small>
                      </div>
                      <div className="progress" style={{ height: '8px' }}>
                        <div 
                          className="progress-bar bg-primary" 
                          role="progressbar" 
                          style={{ width: `${calculateEffectiveness(auditoryValue, 75)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <h5 className="text-center mb-2">Kinesthetic Learning</h5>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-hand-index-thumb-fill text-warning fs-3 me-2"></i>
                      <div className="w-100">
                        <input 
                          type="range" 
                          className="form-range"
                          min="0" 
                          max="100" 
                          value={kinestheticValue} 
                          onChange={(e) => handleStyleChange('kinesthetic', e.target.value)} 
                        />
                        <div className="d-flex justify-content-between">
                          <span className="small">Low</span>
                          <span className="fw-bold">{kinestheticValue}%</span>
                          <span className="small">High</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="d-flex justify-content-between">
                        <small>Effectiveness:</small>
                        <small>{calculateEffectiveness(kinestheticValue, 70).toFixed(0)}%</small>
                      </div>
                      <div className="progress" style={{ height: '8px' }}>
                        <div 
                          className="progress-bar bg-warning" 
                          role="progressbar" 
                          style={{ width: `${calculateEffectiveness(kinestheticValue, 70)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-6 mb-3">
                    <h5 className="text-center mb-2">Reading/Writing</h5>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-journal-text text-info fs-3 me-2"></i>
                      <div className="w-100">
                        <input 
                          type="range" 
                          className="form-range"
                          min="0" 
                          max="100" 
                          value={readingWritingValue} 
                          onChange={(e) => handleStyleChange('reading-writing', e.target.value)} 
                        />
                        <div className="d-flex justify-content-between">
                          <span className="small">Low</span>
                          <span className="fw-bold">{readingWritingValue}%</span>
                          <span className="small">High</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-2">
                      <div className="d-flex justify-content-between">
                        <small>Effectiveness:</small>
                        <small>{calculateEffectiveness(readingWritingValue, 80).toFixed(0)}%</small>
                      </div>
                      <div className="progress" style={{ height: '8px' }}>
                        <div 
                          className="progress-bar bg-info" 
                          role="progressbar" 
                          style={{ width: `${calculateEffectiveness(readingWritingValue, 80)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-3 p-3 bg-light rounded">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1">Dominant Learning Style:</h6>
                      <span className="badge bg-primary px-3 py-2">{dominantStyle.name}</span>
                    </div>
                    <div className="text-end">
                      <h6 className="mb-1">Overall Effectiveness:</h6>
                      <span className="badge bg-success px-3 py-2">{dominantStyle.effectiveness.toFixed(0)}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {error && (
          <div className="alert alert-danger" role="alert">{error}</div>
        )}
        
        {neuralNetworkVisible && (
          <div className="card mb-4">
            <div className="card-header">Neural Network Visualization</div>
            <div className="card-body">
              <div className="neural-visualization p-3 rounded" style={{ backgroundColor: '#f0f4f8', height: '400px', position: 'relative' }}>
                <canvas id="neural-canvas" ref={canvasRef} width="800" height="380"></canvas>
                
                <div className="neural-stats position-absolute top-0 end-0 p-2 m-2 bg-white rounded shadow-sm" style={{ width: '220px' }}>
                  <h6 className="border-bottom pb-2">Neural Development Stats</h6>
                  <div className="d-flex justify-content-between mb-1">
                    <span>Active Neurons:</span>
                    <span className="fw-bold">{42 + (steps * 5)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <span>Connections:</span>
                    <span className="fw-bold">{128 + (steps * 12)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <span>Learning Paths:</span>
                    <span className="fw-bold">{7 + (steps * 2)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <span>Strategy Neurons:</span>
                    <span className="fw-bold">{16 + (steps * 3)}</span>
                  </div>
                  <div className="mt-2 border-top pt-2">
                    <div className="progress mb-1" style={{ height: '15px' }}>
                      <div className="progress-bar bg-success" role="progressbar" style={{ width: `${visualValue}%` }}>Visual</div>
                    </div>
                    <div className="progress mb-1" style={{ height: '15px' }}>
                      <div className="progress-bar bg-info" role="progressbar" style={{ width: `${auditoryValue}%` }}>Auditory</div>
                    </div>
                    <div className="progress mb-1" style={{ height: '15px' }}>
                      <div className="progress-bar bg-warning" role="progressbar" style={{ width: `${kinestheticValue}%` }}>Kinesthetic</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {plan && (
          <div className="card mb-4">
            <div className="card-header">Teaching Plan</div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-8">
                  <div className="plan-details">
                    <div className="plan-header mb-3">
                      <div className="row">
                        <div className="col-md-6">
                          <h6 className="mb-1">Target Taxonomy Level:</h6>
                          <span className="badge bg-primary px-3 py-2">{plan.target_taxonomy.charAt(0).toUpperCase() + plan.target_taxonomy.slice(1)}</span>
                        </div>
                        <div className="col-md-6">
                          <h6 className="mb-1">Planning Method:</h6>
                          <span className="badge bg-secondary px-3 py-2">{planningMethods.find(m => m.id === planningMethod)?.name}</span>
                        </div>
                      </div>
                    </div>
                    
                    <h6 className="mb-3">Plan Steps:</h6>
                    <ol className="list-group list-group-numbered">
                      {plan.readable_plan.map((step, index) => (
                        <li key={index} className="list-group-item d-flex justify-content-between align-items-start">
                          <div className="ms-2 me-auto">
                            <div className="fw-bold">{step.charAt(0).toUpperCase() + step.slice(1)}</div>
                            <span className="text-muted">Expected outcome: +{(dominantStyle.effectiveness / 100 * (index + 2)).toFixed(1)} knowledge points</span>
                          </div>
                          <span className="badge bg-primary rounded-pill">{index + 1}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card">
                    <div className="card-header bg-light">Predicted Outcomes</div>
                    <div className="card-body">
                      <div className="mb-3">
                        <h6 className="mb-2">Knowledge Gain:</h6>
                        <div className="progress" style={{ height: '20px' }}>
                          <div 
                            className="progress-bar bg-success" 
                            role="progressbar" 
                            style={{ width: `${Math.min(100, studentState[0] + (dominantStyle.effectiveness / 100 * steps * 10))}%` }}
                          >
                            {Math.min(100, studentState[0] + (dominantStyle.effectiveness / 100 * steps * 10)).toFixed(0)}%
                          </div>
                        </div>
                      </div>
                      <div className="mb-3">
                        <h6 className="mb-2">Engagement:</h6>
                        <div className="progress" style={{ height: '20px' }}>
                          <div 
                            className="progress-bar bg-primary" 
                            role="progressbar" 
                            style={{ width: `${Math.min(100, studentState[1] + (dominantStyle.effectiveness / 100 * steps * 5))}%` }}
                          >
                            {Math.min(100, studentState[1] + (dominantStyle.effectiveness / 100 * steps * 5)).toFixed(0)}%
                          </div>
                        </div>
                      </div>
                      <div>
                        <h6 className="mb-2">Confusion:</h6>
                        <div className="progress" style={{ height: '20px' }}>
                          <div 
                            className="progress-bar bg-danger" 
                            role="progressbar" 
                            style={{ width: `${Math.max(0, studentState[2] - (dominantStyle.effectiveness / 100 * steps * 8))}%` }}
                          >
                            {Math.max(0, studentState[2] - (dominantStyle.effectiveness / 100 * steps * 8)).toFixed(0)}%
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Student Response Modal */}
      {showStudentResponseModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Student Response Simulation</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowStudentResponseModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-4">
                  <h6>Teaching Scenario:</h6>
                  <div className="alert alert-info">
                    <p className="mb-0">When teaching a student with <strong>{dominantStyle.name} Learning</strong> preference 
                    about solving quadratic equations, the tutor adapts by using {dominantStyle.name.toLowerCase()}-focused strategies.</p>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="card border-success h-100">
                      <div className="card-header bg-success text-white">Tutor Using {dominantStyle.name} Learning Style</div>
                      <div className="card-body">
                        <p><strong>Tutor:</strong> {
                          dominantStyle.name === 'Visual' 
                            ? "Let's look at this diagram that shows how the quadratic formula works. Notice how the parabola intersects the x-axis at these two points..."
                            : dominantStyle.name === 'Auditory'
                              ? "Listen carefully as I explain the steps of the quadratic formula. First we identify a, b, and c, then substitute them into the formula..."
                              : dominantStyle.name === 'Kinesthetic'
                                ? "Let's work through this quadratic equation together. Take your pencil and follow along as we substitute the values..."
                                : "Write down the quadratic formula in your notes: x = (-b ± √(b² - 4ac)) ÷ 2a. Now, let's read through the steps..."
                        }</p>
                        <p><strong>Student Response:</strong> <span className="text-success">This makes sense! I understand how to apply the formula now.</span></p>
                        <p><strong>Learning Effectiveness:</strong> <span className="badge bg-success">{dominantStyle.effectiveness.toFixed(0)}%</span></p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border-danger h-100">
                      <div className="card-header bg-danger text-white">Tutor Not Using {dominantStyle.name} Learning Style</div>
                      <div className="card-body">
                        <p><strong>Tutor:</strong> {
                          dominantStyle.name !== 'Visual' 
                            ? "Let's look at this diagram that shows how the quadratic formula works. Notice how the parabola intersects the x-axis at these two points..."
                            : dominantStyle.name !== 'Auditory'
                              ? "Listen carefully as I explain the steps of the quadratic formula. First we identify a, b, and c, then substitute them into the formula..."
                              : dominantStyle.name !== 'Kinesthetic'
                                ? "Let's work through this quadratic equation together. Take your pencil and follow along as we substitute the values..."
                                : "Write down the quadratic formula in your notes: x = (-b ± √(b² - 4ac)) ÷ 2a. Now, let's read through the steps..."
                        }</p>
                        <p><strong>Student Response:</strong> <span className="text-danger">I'm still confused. Can you explain it differently?</span></p>
                        <p><strong>Learning Effectiveness:</strong> <span className="badge bg-danger">35%</span></p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="card bg-light">
                  <div className="card-body">
                    <h6>Adaptation Impact:</h6>
                    <ul className="mb-0">
                      <li>Knowledge acquisition speed increases by {(dominantStyle.effectiveness - 35).toFixed(0)}%</li>
                      <li>Student engagement increases by {(dominantStyle.effectiveness - 40).toFixed(0)}%</li>
                      <li>Confusion decreases by {(dominantStyle.effectiveness - 30).toFixed(0)}%</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowStudentResponseModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AITrainer;